import { Recipe } from '../types/mealPlan';

/**
 * Reads the server-embedded recipe blob (`#__RECIPE__`) that the SSR/OG
 * middleware injects on a direct page load. This lets the recipe page render
 * its full content synchronously on first paint — no loading skeleton, no
 * content flash — instead of waiting on a client fetch.
 *
 * The blob only corresponds to the URL the server rendered, so it is keyed to
 * the initial pathname: after a client-side navigation to a different recipe
 * the seed no longer applies and the page falls back to its fetch path.
 */
let cachedSeed: { recipe: Recipe; path: string } | null | undefined;

const readSeed = (): { recipe: Recipe; path: string } | null => {
  if (cachedSeed !== undefined) return cachedSeed;
  cachedSeed = null;
  if (typeof document === 'undefined') return cachedSeed;
  try {
    const el = document.getElementById('__RECIPE__');
    const text = el?.textContent;
    if (text) {
      const recipe = JSON.parse(text) as Recipe;
      if (recipe && recipe.id) {
        cachedSeed = { recipe, path: window.location.pathname };
      }
    }
  } catch {
    cachedSeed = null;
  }
  return cachedSeed;
};

/**
 * Returns the server-seeded recipe if one was embedded for the given pathname,
 * otherwise null. Safe to call repeatedly; the blob is parsed once and cached.
 */
export const getRecipeSeed = (pathname: string): Recipe | null => {
  const seed = readSeed();
  return seed && seed.path === pathname ? seed.recipe : null;
};
